import React from 'react';
import './Sidebar.css';

const availableFields = [
  "broadcast_series_title",
  "title",
  "teaser_text",
  "keywords"
];

const Sidebar = ({
  isOpen,
  sliderValue,
  onSliderChange,
  maxResults,
  onMaxResultsChange,
  selectedFields,
  setSelectedFields,
  fieldBoosts,
  setFieldBoosts,
  docTypeBoosts,
  setDocTypeBoosts,
  filters,
  setFilters,
  notFilters,
  setNotFilters,
  localWeight,
}) => {

  const handleSliderInputChange = (e) => {
    let value = parseFloat(e.target.value) || 0;
    value = Math.min(10000, Math.max(0, value));
    onSliderChange({ target: { value } });
  };

  const toggleSearchField = (field) => {
    setSelectedFields((prev) =>
      prev.includes(field)
        ? prev.filter((f) => f !== field)
        : [...prev, field]
    );
  };

  const handleBoostChange = (field, value) => {
    setFieldBoosts((prev) => ({
      ...prev,
      [field]: parseInt(value, 10) || 1,
    }));
  };

  const addDocTypeBoost = () => {
    setDocTypeBoosts([...docTypeBoosts, { value: '', boost: 1 }]);
  };

  const updateDocTypeBoost = (index, key, value) => {
    const newDocTypeBoosts = [...docTypeBoosts];
    newDocTypeBoosts[index] = {
      ...newDocTypeBoosts[index],
      [key]: value,
    };
    setDocTypeBoosts(newDocTypeBoosts);
  };

  const addFilter = () => {
    setFilters([...filters, { key: '', value: '' }]);
  };

  const updateFilter = (index, key, value) => {
    const newFilters = [...filters];
    newFilters[index] = { key, value };
    setFilters(newFilters);
  };

  const addNotFilter = () => {
    setNotFilters([...notFilters, { key: '', value: '' }]);
  };

  const updateNotFilter = (index, key, value) => {
    const newNotFilters = [...notFilters];
    newNotFilters[index] = { key, value };
    setNotFilters(newNotFilters);
  };


  return (
    <div className={`Sidebar ${isOpen ? 'open' : 'closed'}`}>
      <div className="sidebar-header">
        <h2>Developer Menü</h2>
      </div>

      <div className="api-banner">
        Diese UI verwendet die neue Hybrid-Search-API, bereitgestellt von Modul 14.
      </div>

      <div className="sidebar-content">
        <label htmlFor="slider">Similarity Threshold:</label>
        <div className="slider-container">
          <div className="slider-input-wrapper">
            <input
              type="number"
              className="slider-number-input"
              min="0"
              max="10000"
              step="1"
              value={sliderValue}
              onChange={handleSliderInputChange}
            />
          </div>
          <div className="slider-control">
            <button
              type="button"
              onClick={() =>
                onSliderChange({
                  target: { value: Math.max(0, sliderValue - 1000) },
                })
              }
            >
              –
            </button>
            <input
              type="range"
              id="slider"
              min="0"
              max="10000"
              step="1"
              value={sliderValue}
              onChange={onSliderChange}
            />
            <button
              type="button"
              onClick={() =>
                onSliderChange({
                  target: { value: Math.min(10000, Number(sliderValue) + 1000) },
                })
              }
            >
              +
            </button>
          </div>
        </div>

        <label htmlFor="maxResults">Maximale Anzahl Ergebnisse:</label>
        <input
          type="number"
          id="maxResults"
          className="maxResults-number-input"
          min="1"
          value={maxResults}
          onChange={onMaxResultsChange}
        />
      </div>

      <div className="hybrid-section">
        <h3>Hybrid Search Parameter</h3>

        <div className="disabled-section">
          <label className="section-label disabled-label">Keyword vs. Semantik Gewichtung:</label>
          <div className="keyword-weight">
             <div className="disabled-text">
              Die Gewichtung wird jetzt per OpenSearch Pipeline fest vorgegeben und kann hier nicht mehr verändert werden.
            </div>
            <div className="slider-control">
              <span>💡</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={localWeight}
                disabled
              />
              <span>🔍</span>
            </div>
            <div className="disabled-text">
              Eingestellt: {Math.round(localWeight * 100)}% Keyword / {Math.round((1 - localWeight) * 100)}% Semantik
            </div>
          </div>
        </div>

        <div className="section-label">Suchfelder:</div>
        <div className="checkbox-group">
          {availableFields.map((field) => (
            <label key={field} className="checkbox-label">
              <input
                type="checkbox"
                checked={selectedFields.includes(field)}
                onChange={() => toggleSearchField(field)}
              />
              <span>{field}</span>
            </label>
          ))}
        </div>

        {selectedFields.length > 0 && (
          <>
            <div className="section-label">Feldgewichtung:</div>
            {selectedFields.map((field) => (
              <div key={field} className="boost-row">
                <span className="boost-label">{field}</span>
                <input
                  type="number"
                  className="boost-input"
                  min="1"
                  value={fieldBoosts[field] ?? 1}
                  onChange={(e) => handleBoostChange(field, e.target.value)}
                />
              </div>
            ))}
          </>
        )}
        <div className="section-label">Filter Key-Value Paare:</div>
        {filters.map((filter, index) => (
          <div key={index} className="filter-pair">
            <input
              type="text"
              placeholder="Key"
              value={filter.key}
              onChange={(e) =>
                updateFilter(index, e.target.value, filter.value)
              }
            />
            <input
              type="text"
              placeholder="Value"
              value={filter.value}
              onChange={(e) =>
                updateFilter(index, filter.key, e.target.value)
              }
            />
            <button
              type="button"
              className="remove-filter-button"
              onClick={() =>
                setFilters(filters.filter((_, i) => i !== index))
              }
            >
              ✕
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={addFilter}
          className="add-filter-button"
        >
          + Filter hinzufügen
        </button>

        <div className="section-label">Not-Filter Key-Value Paare:</div>
        {notFilters.map((filter, index) => (
          <div key={index} className="filter-pair">
            <input
              type="text"
              placeholder="Key"
              value={filter.key}
              onChange={(e) =>
                updateNotFilter(index, e.target.value, filter.value)
              }
            />
            <input
              type="text"
              placeholder="Value"
              value={filter.value}
              onChange={(e) =>
                updateNotFilter(index, filter.key, e.target.value)
              }
            />
            <button
              type="button"
              className="remove-filter-button"
              onClick={() =>
                setNotFilters(notFilters.filter((_, i) => i !== index))
              }
            >
              ✕
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={addNotFilter}
          className="add-filter-button"
        >
          + Not-Filter hinzufügen
        </button>

        <div className='doc_type_boost'>
          <div className="section-label with-info">
            Document Type Boosting:
            <span className="info-icon" tabIndex={0}>
              ℹ️
              <span className="info-tooltip">
                Dieses Feature wird (Stand 29.10.2026) noch nicht produktiv vom KiKA verwendet.
              </span>
            </span>
          </div>
          {docTypeBoosts.map((docTypeBoost, index) => (
            <div key={index} className="doc-type-boost-row">
              <input
                type="text"
                placeholder="Document type"
                value={docTypeBoost.value}
                onChange={(e) =>
                  updateDocTypeBoost(index, 'value', e.target.value)
                }
              />
              <input
                type="number"
                className="doc-type-boost-input"
                min="0"
                step="0.01"
                placeholder="Boost"
                value={docTypeBoost.boost}
                onChange={(e) =>
                  updateDocTypeBoost(index, 'boost', e.target.value)
                }
              />
              <button
                type="button"
                className="remove-filter-button"
                onClick={() =>
                  setDocTypeBoosts(docTypeBoosts.filter((_, i) => i !== index))
                }
              >
                ✕
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={addDocTypeBoost}
            className="add-filter-button"
          >
            + Document Type hinzufügen
          </button>
        </div>

      </div>
    </div>
  );
};

export default Sidebar;
